import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as pdfjsLib from 'pdfjs-dist';
import './PdfCanvasViewer.css';

/* Renders a PDF ourselves, to <canvas>, instead of handing the file to the
 * browser.
 *
 * WHY THIS EXISTS. Serving a PDF with `Content-Disposition: inline` — whether
 * in a tab or in an <iframe src="blob:…"> — invokes Chrome's built-in PDF
 * viewer, which now offers "Summarize with Gemini" on every document. That is a
 * one-click path from a client's IPS to a third-party AI, with no audit trail
 * on our side, on a corpus of 44k migrated client records. No response header
 * suppresses it; the only reliable fix is to never give the browser a PDF
 * document to render. Chrome sees a canvas, so its PDF viewer never engages.
 *
 * The worker is `/pdf.worker.js` from `public/` — pdf.js 3.11.174, byte-matching
 * the pinned `pdfjs-dist`. Deliberately NOT the cdnjs URL the older
 * routes/PDFViewer.js used: a compliance-driven viewer should not depend on a
 * third-party CDN being up, or on it serving what we think it serves.
 *
 * Text layer is not decoration. Chrome's viewer gives advisors select, copy and
 * ctrl-F, and ~86% of this corpus is read-not-edited (see
 * hub-architecture-and-crm-consolidation.md §3) — "I need three values out of
 * it" is the dominant workflow. A canvas-only viewer would be a downgrade they
 * would rightly reject, so the invisible text layer is what makes this a
 * replacement rather than a restriction.
 */
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.js';

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];

export default function PdfCanvasViewer({ data, filename, onDownload }) {
  const containerRef = useRef(null);
  const pdfRef = useRef(null);
  // pdf.js render tasks are cancellable; a zoom change mid-render must cancel
  // the in-flight pass or two passes fight over the same canvas.
  const renderRunRef = useRef(0);

  const [numPages, setNumPages] = useState(0);
  const [rendered, setRendered] = useState(0);
  const [scale, setScale] = useState(1.25);
  const [error, setError] = useState(null);

  // 1. Parse the document once. `data` is an ArrayBuffer we already hold, so
  //    there is no second network fetch — which matters because the one-time
  //    token backing it has already been burned by the time we get here.
  useEffect(() => {
    let cancelled = false;
    let task = null;
    (async () => {
      try {
        // pdf.js takes ownership of (and detaches) the buffer it is given, so
        // hand it a copy — otherwise a re-parse, or the download button, finds
        // a zero-length buffer.
        task = pdfjsLib.getDocument({ data: data.slice(0) });
        const pdf = await task.promise;
        if (cancelled) { pdf.destroy(); return; }
        pdfRef.current = pdf;
        setNumPages(pdf.numPages);
      } catch (e) {
        if (!cancelled) setError('This file could not be read as a PDF.');
      }
    })();
    return () => {
      cancelled = true;
      if (task) task.destroy?.();
      if (pdfRef.current) { pdfRef.current.destroy(); pdfRef.current = null; }
    };
  }, [data]);

  // 2. (Re-)render every page at the current scale.
  const renderAll = useCallback(async () => {
    const pdf = pdfRef.current;
    const container = containerRef.current;
    if (!pdf || !container) return;

    const run = ++renderRunRef.current;
    container.replaceChildren();
    setRendered(0);

    const dpr = window.devicePixelRatio || 1;
    for (let n = 1; n <= pdf.numPages; n += 1) {
      if (run !== renderRunRef.current) return;      // superseded by a newer run
      const page = await pdf.getPage(n);
      if (run !== renderRunRef.current) return;
      const viewport = page.getViewport({ scale });

      const wrap = document.createElement('div');
      wrap.className = 'pdfPage';
      wrap.style.cssText =
        `position:relative;margin:0 auto 14px;width:${Math.floor(viewport.width)}px;` +
        `height:${Math.floor(viewport.height)}px;box-shadow:0 2px 10px rgba(10,41,71,.18);background:#fff;`;
      // pdf.js 3.x positions text-layer spans off this custom property.
      wrap.style.setProperty('--scale-factor', String(scale));

      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.cssText = `width:${Math.floor(viewport.width)}px;height:${Math.floor(viewport.height)}px;display:block;`;
      wrap.appendChild(canvas);

      const textLayer = document.createElement('div');
      textLayer.className = 'textLayer';
      wrap.appendChild(textLayer);
      container.appendChild(wrap);

      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      await page.render({ canvasContext: ctx, viewport }).promise;
      if (run !== renderRunRef.current) return;

      // Invisible, selectable text over the bitmap — this is what gives back
      // select/copy and the browser's own ctrl-F.
      try {
        const textContent = await page.getTextContent();
        await pdfjsLib.renderTextLayer({
          textContentSource: textContent, container: textLayer, viewport, textDivs: [],
        }).promise;
      } catch { /* a page with no extractable text is fine, the bitmap stands */ }

      setRendered(n);
    }
  }, [scale]);

  useEffect(() => { renderAll(); }, [renderAll, numPages]);

  const zoomBy = (dir) => {
    const i = ZOOM_STEPS.indexOf(scale);
    const next = ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, Math.max(0, (i < 0 ? 2 : i) + dir))];
    if (next) setScale(next);
  };

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#b42318' }}>
        {error}
        {onDownload && (
          <div className="mt-3">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onDownload}>
              <i className="bi bi-download me-1" /> Download instead
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '.5rem', flexShrink: 0,
        padding: '.4rem .75rem', borderBottom: '1px solid #dde2e8', background: '#f7f9fb',
        fontSize: '.8rem', color: '#4b5563',
      }}>
        <button type="button" className="btn btn-sm btn-light" onClick={() => zoomBy(-1)} aria-label="Zoom out">
          <i className="bi bi-zoom-out" />
        </button>
        <span style={{ minWidth: 44, textAlign: 'center' }}>{Math.round(scale * 100)}%</span>
        <button type="button" className="btn btn-sm btn-light" onClick={() => zoomBy(1)} aria-label="Zoom in">
          <i className="bi bi-zoom-in" />
        </button>
        <span style={{ marginLeft: '.5rem' }}>
          {numPages ? (rendered < numPages ? `Rendering ${rendered}/${numPages}…` : `${numPages} page${numPages > 1 ? 's' : ''}`) : 'Loading…'}
        </span>
        <span style={{ flex: 1 }} />
        {onDownload && (
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onDownload}>
            <i className="bi bi-download me-1" /> Download
          </button>
        )}
      </div>
      <div
        ref={containerRef}
        style={{ flex: 1, overflow: 'auto', background: '#525659', padding: '14px 0' }}
        aria-label={filename || 'Document'}
      />
    </div>
  );
}

PdfCanvasViewer.propTypes = {
  /** The PDF bytes. Held in memory — never re-fetched. */
  data: PropTypes.instanceOf(ArrayBuffer).isRequired,
  filename: PropTypes.string,
  onDownload: PropTypes.func,
};
